import { useEffect, useRef, useState } from "react";
import Tooltip from "../Tooltip";

interface Option {
  value: string;
  label: string;
}

interface DropdownProps {
  name: string;
  label: string;
  value: string;
  showToolTip?: boolean;
  toolTipText?: string;
  options: Option[];
  onChange: (event: { target: { value: string } }) => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  name,
  label,
  value,
  showToolTip,
  toolTipText,
  options,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasValue, setHasValue] = useState(value !== "");
  const [tooltipPosition, setTooltipPosition] = useState(0);
  const [tooltipReady, setTooltipReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const calculatePosition = () => {
      if (labelRef.current) {
        const labelWidth = labelRef.current.getBoundingClientRect().width;
        setTooltipPosition(12 + labelWidth + 4);
        setTimeout(() => setTooltipReady(true), 100);
      }
    };

    requestAnimationFrame(calculatePosition);
  }, [label, hasValue]);

  const handleSelect = (selectedValue: string) => {
    setIsOpen(false);
    setHasValue(true);
    onChange({ target: { value: selectedValue } });
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        style={{
          boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.07) inset",
        }}
        className="block w-full appearance-none rounded-[8px] border border-[#E2E2E2] bg-[#F8F8F8] px-[12px] h-[56px] pt-[24px] text-[18px] font-normal leading-[127%] text-black cursor-pointer"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {value ? options.find((opt) => opt.value === value)?.label : ""}
      </div>

      <label
        ref={labelRef}
        htmlFor={name}
        className={`absolute left-3 ${hasValue ? "top-[2px]" : "top-[16px]"} ${
          isOpen ? "z-[101]" : "z-[104]"
        } origin-[0] transform text-[16px] text-gray-500 transition-all duration-200 ${
          hasValue ? "scale-[0.9]" : "scale-100"
        } cursor-pointer`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {label}
      </label>

      {showToolTip && toolTipText && tooltipReady && (
        <div
          className={`absolute ${
            hasValue ? "top-[2px]" : "top-[16px]"
          } ${isOpen ? "z-[106]" : "z-[104]"} flex items-center`}
          style={{
            left: `${tooltipPosition}px`,
            transform: hasValue ? "scale(0.9)" : "scale(1)",
            transformOrigin: "left center",
            transition: "all 200ms",
          }}
        >
          <span className="text-[18px]">
            <Tooltip text={toolTipText} zIndex={isOpen ? 106 : 103} />
          </span>
        </div>
      )}

      {isOpen && (
        <ul className="dropdown-scrollable absolute z-[105] mt-1 w-full rounded-[8px] border border-[#E2E2E2] bg-white shadow-md max-h-[200px] overflow-y-auto">
          {options.map((option) => (
            <li
              key={option.value}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-[16px]"
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
